import { Boundary } from './index';
import { Cells, Geometry } from '../index';
import { ICells } from '../../geometry/Cells.type';
import { INrchop, IRechargeBoundary, IRechargeBoundaryExport } from './RechargeBoundary.type';
import { ISpValues, IValueProperty } from './Boundary.type';
import { Polygon } from 'geojson';
import { cloneDeep } from 'lodash';
import BoundingBox from '../../geometry/BoundingBox';
import GridSize from '../../geometry/GridSize';
import Stressperiods from '../Stressperiods';
import Uuid from 'uuid';

export default class RechargeBoundary extends Boundary {

  get type() {
    return this._props.properties.type;
  }

  get id() {
    return this._props.id;
  }

  set id(value) {
    this._props.id = value;
  }

  get geometry() {
    return Geometry.fromObject(this._props.geometry);
  }

  set geometry(value) {
    this._props.geometry = value.toObject() as Polygon;
  }

  get name() {
    return this._props.properties.name;
  }

  set name(value) {
    this._props.properties.name = value;
  }

  get layers() {
    return this._props.properties.layers;
  }

  set layers(value) {
    this._props.properties.layers = value;
  }

  get cells() {
    return Cells.fromObject(this._props.properties.cells);
  }

  set cells(value) {
    this._props.properties.cells = value.toObject();
  }

  get geometryType() {
    return RechargeBoundary.geometryType();
  }

  get nrchop(): INrchop {
    return this._props.properties.nrchop;
  }

  set nrchop(value: INrchop) {
    this._props.properties.nrchop = value;
  }

  get optionCode(): INrchop {
    return this._props.properties.nrchop;
  }

  set optionCode(value: INrchop) {
    this._props.properties.nrchop = value;
  }

  get isExcludedFromCalculation(): boolean {
    return this._props.properties.isExcludedFromCalculation ?? false;
  }

  set isExcludedFromCalculation(isExcluded) {
    this._props.properties.isExcludedFromCalculation = isExcluded;
  }

  public static geometryType() {
    return 'Polygon';
  }

  public static fromExport(obj: IRechargeBoundaryExport, boundingBox: BoundingBox, gridSize: GridSize) {
    return this.create(
      obj.id ? obj.id : Uuid.v4(),
      obj.geometry,
      obj.name,
      obj.layers,
      obj.cells || Cells.fromGeometry(Geometry.fromGeoJson(obj.geometry), boundingBox, gridSize).toObject(),
      obj.sp_values,
      obj.nrchop,
      obj.is_excluded_from_calculation,
    );
  }

  public static fromObject(obj: IRechargeBoundary) {
    return new this(obj);
  }

  public static valueProperties(): IValueProperty[] {
    return [
      {
        name: 'Recharge rate',
        description: 'Recharge rate into layer',
        unit: 'm/day',
        decimals: 5,
        default: 0,
        canBe2D: true,
      },
    ];
  }

  public static create(
    id: string,
    geometry: Polygon,
    name: string,
    layers: number[],
    cells: ICells,
    spValues: ISpValues,
    nrchop: INrchop = 1,
    isExcludedFromCalculation = false,
  ) {

    return new this({
      id,
      type: 'Feature',
      geometry,
      properties: {
        type: 'rch',
        name,
        cells,
        layers,
        sp_values: spValues,
        nrchop,
        isExcludedFromCalculation,
      },
    });
  }

  constructor(props: IRechargeBoundary) {
    super();
    this._props = cloneDeep(props);
    this._class = RechargeBoundary;
  }

  public getSpValues(stressPeriods: Stressperiods) {
    return Boundary.mergeStressperiodsWithSpValues(stressPeriods, this._props.properties.sp_values);
  }

  public setSpValues(spValues: ISpValues) {
    this._props.properties.sp_values = spValues;
  }

  public recalculateCells(boundingBox: BoundingBox, gridSize: GridSize): void {
    this.cells = Cells.fromGeometry(this.geometry, boundingBox, gridSize);
  }

  public toExport = (stressPeriods: Stressperiods): IRechargeBoundaryExport => ({
    id: this.id,
    type: this.type,
    name: this.name,
    geometry: this.geometry.toObject() as Polygon,
    cells: this.cells.toObject(),
    layers: this.layers,
    nrchop: this.nrchop ? this.nrchop : 1,
    sp_values: this.getSpValues(stressPeriods),
    is_excluded_from_calculation: this.isExcludedFromCalculation,
  });

  public toObject(): IRechargeBoundary {
    return this._props;
  }

  public get valueProperties(): IValueProperty[] {
    return RechargeBoundary.valueProperties();
  }
}
